import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, Building2, Users, Trophy, CreditCard, Tag,
  DollarSign, Menu, X, LogOut, Shield, Loader2, Handshake, Megaphone, Wallet, HandCoins, Zap, FileWarning,
  ClipboardList, Activity, Database, HelpCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { LOGO_URL } from './admin/adminConstants';
import AdminDashboardTab from './admin/AdminDashboardTab';
import AdminCompaniesTab from './admin/AdminCompaniesTab';
import AdminAffiliatesTab from './admin/AdminAffiliatesTab';
import AdminRankingTab from './admin/AdminRankingTab';
import AdminCouponsTab from './admin/AdminCouponsTab';
import AdminPlansTab from './admin/AdminPlansTab';
import AdminCommissionsTab from './admin/AdminCommissionsTab';
import AdminPartnersTab from './admin/AdminPartnersTab';
import AdminCampaignsTab from './admin/AdminCampaignsTab';
import AdminCostsTab from './admin/AdminCostsTab';
import AdminSponsorshipsTab from './admin/AdminSponsorshipsTab';
import AdminImplantTab from './admin/AdminImplantTab';
import AdminPendingDocsTab from './admin/AdminPendingDocsTab';
import AdminContactsTab from './admin/AdminContactsTab';
import AdminRegistryTab from './admin/AdminRegistryTab';
import AdminActivationTab from './admin/AdminActivationTab';
import AdminHelpSearchesTab from './admin/AdminHelpSearchesTab';
import { HelpButton } from '@/components/HelpButton';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'novo-cliente', label: 'Novo Cliente', icon: Zap },
  { id: 'empresas', label: 'Empresas', icon: Building2 },
  { id: 'cadastro-empresas', label: 'Cadastro de Empresas', icon: ClipboardList },
  { id: 'ativacao', label: 'Ativação', icon: Activity },
  { id: 'banco-dados', label: 'Base Geral de Usuários', icon: Database },
  { id: 'aguardando-cnpj', label: 'Aguardando CNPJ', icon: FileWarning },
  { id: 'afiliados', label: 'Afiliados', icon: Users },
  { id: 'parceiros', label: 'Parceiros', icon: Handshake },
  { id: 'patrocinios', label: 'Patrocínios', icon: HandCoins },
  { id: 'campanhas', label: 'Campanhas', icon: Megaphone },
  { id: 'ranking', label: 'Ranking', icon: Trophy },
  { id: 'cupons', label: 'Cupons', icon: Tag },
  { id: 'planos', label: 'Planos', icon: CreditCard },
  { id: 'comissoes', label: 'Comissoes', icon: DollarSign },
  { id: 'custos', label: 'Custos', icon: Wallet },
  { id: 'duvidas', label: 'Dúvidas na Ajuda', icon: HelpCircle },
];

const AdminMaster = () => {
  const { user, login, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);

  const isAdmin = user?.email === 'marcelinojoserodrigues@gmail.com';

  // Admin login form
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-[#C5A059] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A2647] via-[#0A2647] to-[#1a3a5c] flex items-center justify-center p-4">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 w-full max-w-md shadow-2xl" data-testid="admin-login-form">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="FaloComODono" className="h-14 mx-auto mb-4 object-contain" />
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/20 rounded-full px-4 py-1.5 mb-3">
              <Shield className="w-4 h-4 text-[#C5A059]" />
              <span className="text-sm font-semibold text-[#C5A059]">Painel Master</span>
            </div>
            <p className="text-sm text-white/50">Acesso restrito ao administrador</p>
          </div>
          <div className="space-y-4">
            <input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)}
              placeholder="Email do administrador"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#C5A059]/50"
              data-testid="admin-email-input" />
            <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)}
              placeholder="Senha"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#C5A059]/50"
              onKeyDown={e => e.key === 'Enter' && handleAdminLogin()}
              data-testid="admin-password-input" />
            <Button onClick={handleAdminLogin} disabled={adminLoginLoading}
              className="w-full bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-bold py-3 rounded-xl text-sm"
              data-testid="admin-login-btn">
              {adminLoginLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Acessar Painel Master'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  async function handleAdminLogin() {
    if (!adminEmail || !adminPassword) return;
    setAdminLoginLoading(true);
    try {
      await login(adminEmail, adminPassword);
    } catch { /* toast handled by AuthContext or login itself */ }
    finally { setAdminLoginLoading(false); }
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard': return <AdminDashboardTab />;
      case 'novo-cliente': return <AdminImplantTab />;
      case 'empresas': return <AdminCompaniesTab />;
      case 'cadastro-empresas': return <AdminRegistryTab />;
      case 'ativacao': return <AdminActivationTab />;
      case 'banco-dados': return <AdminContactsTab />;
      case 'aguardando-cnpj': return <AdminPendingDocsTab />;
      case 'afiliados': return <AdminAffiliatesTab />;
      case 'parceiros': return <AdminPartnersTab />;
      case 'patrocinios': return <AdminSponsorshipsTab />;
      case 'campanhas': return <AdminCampaignsTab />;
      case 'ranking': return <AdminRankingTab />;
      case 'cupons': return <AdminCouponsTab />;
      case 'planos': return <AdminPlansTab />;
      case 'comissoes': return <AdminCommissionsTab />;
      case 'custos': return <AdminCostsTab />;
      case 'duvidas': return <AdminHelpSearchesTab />;
      default: return <AdminDashboardTab />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-master-page">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A2647] border-r border-white/5 transform transition-transform duration-200 lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-5 border-b border-white/5">
            <img src={LOGO_URL} alt="FaloComODono" className="h-10 object-contain mb-2" />
            <p className="text-[10px] text-[#C5A059] font-semibold uppercase tracking-widest">Painel Master</p>
          </div>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${isActive ? 'bg-[#C5A059]/15 text-[#C5A059]' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                  data-testid={`tab-${tab.id}`}>
                  <Icon className="w-4.5 h-4.5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
          <div className="p-3 border-t border-white/5">
            <button onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-400/10 transition-all"
              data-testid="admin-logout-btn">
              <LogOut className="w-4.5 h-4.5" />Sair
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></button>
            <h1 className="text-lg font-bold text-[#0A2647]">{TABS.find(t => t.id === activeTab)?.label || 'Dashboard'}</h1>
          </div>
          <div className="text-xs text-slate-500">{user.email}</div>
        </header>
        <div className="p-4 lg:p-8">
          {renderTab()}
        </div>
        <HelpButton ambiente="master" tela={activeTab} />
      </main>
    </div>
  );
};

export default AdminMaster;
